'use client';
import React, { useRef, useState } from 'react';
import { Box, Link, ListSubheader, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { NavChildLinkType, NavLinkType } from '../types';
import { usePathname } from '../../../../utils/routing';

type Props = {
  link: NavLinkType;
  sx?: object;
};

const NavDropdown = ({ link, sx }: Props) => {
  const pathname = usePathname();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 150);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const commonSx = { m: 1, display: 'flex', alignItems: 'center', ...sx };

  if (!link.children?.length) {
    if (!link.href) return null;
    return (
      <Link
        href={link.href}
        sx={{
          ...commonSx,
          textDecoration: link.href === pathname ? 'underline' : undefined,
        }}
      >
        {link.label}
      </Link>
    );
  }

  const isActive =
    link.href === pathname || link.children.some((c) => c.href === pathname);

  return (
    <Box
      ref={anchorRef}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onKeyDown={(event) => {
        if (event.code === 'Enter') {
          setOpen((prevState) => !prevState);
        }
      }}
      onMouseLeave={scheduleClose}
      sx={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {link.href ? (
        <Link
          href={link.href}
          sx={{
            ...commonSx,
            gap: 0.5,
            textDecoration: isActive ? 'underline' : undefined,
          }}
        >
          {link.label}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: '1rem',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </Link>
      ) : (
        <Box
          component="span"
          sx={{
            ...commonSx,
            gap: 0.5,
            cursor: 'default',
            textDecoration: isActive ? 'underline' : undefined,
          }}
        >
          {link.label}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: '1rem',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
      )}
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        keepMounted
        onClose={() => setOpen(false)}
        disableScrollLock
        style={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: 1, minWidth: 160, pointerEvents: 'auto' },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {link.href && (
          <MenuItem
            key={link.label}
            component={Link}
            href={link.href}
            onClick={() => setOpen(false)}
            sx={{
              textDecoration: link.href === pathname ? 'underline' : undefined,
            }}
          >
            {link.label}
          </MenuItem>
        )}
        {link.children.map((child: NavChildLinkType, idx: number) => {
          const showGroup =
            child.group &&
            (idx === 0 || child.group !== link.children![idx - 1]?.group);
          return (
            <React.Fragment key={child.label}>
              {showGroup && (
                <ListSubheader disableSticky sx={{ fontWeight: '700' }}>
                  {child.group}
                </ListSubheader>
              )}
              <MenuItem
                component={Link}
                href={child.href}
                onClick={() => setOpen(false)}
                sx={{
                  textDecoration:
                    child.href === pathname ? 'underline' : undefined,
                }}
              >
                {child.label}
              </MenuItem>
            </React.Fragment>
          );
        })}
      </Menu>
    </Box>
  );
};

export default NavDropdown;
